import { observable, makeObservable } from "mobx";
import ActualProgressEntry from "./ActualProgressEntry";
import PlannedProgressEntry from "./PlannedProgressEntry";
import Task from "./Task";
import {
  UpdateTaskPackage,
  UpdateActuals,
} from "../operations/TaskPackageOperations";
import { updateParentTaskPackage } from "../util/TaskUtil";
import { isNullOrUndefined } from "../util/HelperUtil";
import { addDays, formatISO, parseISO } from "date-fns";
import ForecastedProgressEntry from "./ForecastedProgressEntry";

export default class TaskPackage {
  project;
  taskPackage;
  id;
  displayIndex;
  name;
  parent;
  parentDisplayIndex;
  forecastMethod;
  constantForcastValue;
  plannedCost;
  plannedDuration;
  plannedStartDate;
  plannedStartPeriod;
  plannedEndDate;
  plannedEndPeriod;
  forecastedEndPeriod;
  costWeightAllocation;
  durationWeightAllocation;
  tasks;
  taskPackages;
  code;
  actualProgress;
  plannedProgress;
  forecastedProgress;
  isPackage = true;
  analysis=[];
  oldId;

  constructor(
    code,
    project,
    taskPackage,
    id,
    displayIndex,
    name,
    parent,
    parentDisplayIndex,
    forecastMethod,
    constantForcastValue,
    plannedCost,
    plannedDuration,
    plannedStartDate,
    plannedStartPeriod,
    plannedEndDate,
    plannedEndPeriod,
    forecastedEndPeriod,
    costWeightAllocation,
    durationWeightAllocation,
    tasks,
    taskPackages,
    plannedProgress,
    actualProgress,
    forecastedProgress
  ) {
    this.code = code;
    this.project = project;
    this.taskPackage = taskPackage;
    this.id = id;
    this.displayIndex = displayIndex;
    this.name = name;
    this.parent = parent;
    this.parentDisplayIndex = parentDisplayIndex;
    this.forecastMethod = forecastMethod;
    this.constantForcastValue = !isNullOrUndefined(constantForcastValue)
      ? constantForcastValue
      : 0;
    this.plannedCost = plannedCost;
    this.plannedDuration = plannedDuration;
    this.plannedStartDate = plannedStartDate;
    this.plannedStartPeriod = plannedStartPeriod;
    this.plannedEndDate = plannedEndDate;
    this.plannedEndPeriod = plannedEndPeriod;
    this.forecastedEndPeriod = forecastedEndPeriod;
    this.costWeightAllocation = !isNullOrUndefined(costWeightAllocation)
      ? costWeightAllocation
      : "inherited";
    this.durationWeightAllocation = !isNullOrUndefined(durationWeightAllocation)
      ? durationWeightAllocation
      : "inherited";
    this.tasks = !isNullOrUndefined(tasks) ? tasks : [];
    this.taskPackages = !isNullOrUndefined(taskPackages) ? taskPackages : [];
    this.plannedProgress = !isNullOrUndefined(plannedProgress)
      ? plannedProgress
      : [];
    this.actualProgress = !isNullOrUndefined(actualProgress)
      ? actualProgress
      : [];
    this.forecastedProgress = !isNullOrUndefined(forecastedProgress)
      ? forecastedProgress
      : [];

    makeObservable(this, {
      project: observable,
      taskPackage: observable,
      id: observable,
      name: observable,
      code: observable,
      forecastMethod: observable,
      constantForcastValue: observable,
      tasks: observable,
      plannedCost: observable,
      plannedDuration: observable,
      plannedStartDate: observable,
      plannedStartPeriod: observable,
      plannedEndDate: observable,
      plannedEndPeriod: observable,
      plannedProgress: observable,
      //forecastedProgress: observable,
      actualProgress: observable,
      taskPackages: observable,
      costWeightAllocation: observable,
      durationWeightAllocation: observable,
      analysis:observable,
    })
  }

  static BasicTaskPackage(
    code,
    project,
    id,
    displayIndex,
    name,
    parent,
    parentDisplayIndex,
    plannedStartDate
  ) {
    let startDate = parseISO(plannedStartDate);
    let endDate = addDays(startDate, 1);
    return new TaskPackage(
      code, //code
      project, //project
      [], //taskPackage
      id, // id
      displayIndex,
      name, //name
      parent, //parent
      parentDisplayIndex,
      "", //forecastMethod
      "", //constantForecastValue
      0, // plannedCost
      0, // plannedDuration
      formatISO(startDate), //planned Start Date
      "", // planned Start Period
      formatISO(endDate), //planned End Date
      "", // planned End Period
      "", // forecasted End Period
      "inherited", //costWeightAllocation
      "inherited", //durationWeightAllocation
      [],
      [],
      [],
      [],
      []
    );
  }

  static RootTaskPackage(project) {
    return new TaskPackage(
      "1", //code
      project, //project
      [], //taskPackage
      "0", //new ObjectId().toHexString(), //id
      "0", //displayIndex
      "Task Root", //name
      "", //parent
      "", //parentDisplayIndex
      "", //forecastMethod
      "", //constantForecastValue
      0, // plannedCost
      0, // plannedDuration
      new Date().toString(), //planned Start Date
      "", // planned End Period
      new Date().toString(), //planned End Date
      "", // planned End Period
      "", // forecasted End Period
      "50", //costWeightAllocation
      "50", //durationWeightAllocation
      [],
      [],
      [],
      [],
      []
    );
  }

  generateUniqueCode() {
    // Group all children tasks/task-packages in current task-package into single array
    let allCodes = [];
    if (this.tasks !== undefined) {
      allCodes = allCodes.concat(this.tasks);
    }
    if (this.taskPackages !== undefined) {
      allCodes = allCodes.concat(this.taskPackages);
    }

    // Sort codes and find maximum trailing number
    let codes = allCodes
      .map((t) => t.code)
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    let maxCode = allCodes.length > 0 ? codes.pop() : this.code + ".0";

    // Increment max code by 1 and return new code
    if (this.parent === "") {
      return (parseInt(maxCode) + 1).toString();
    } else {
      return (
        this.code +
        "." +
        (
          parseInt(maxCode.substring(maxCode.lastIndexOf(".") + 1)) + 1
        ).toString()
      );
    }
  }

  static FixIndex(taskPackage, current) {
    let indexPackage = 0;
    let indexTask = 0;

    while (
      indexPackage < taskPackage.taskPackages.length ||
      indexTask < taskPackage.tasks.length
    ) {
      let packIndex = "1000000";
      let taskIndex = "1000000";

      if (indexPackage < taskPackage.taskPackages.length) {
        packIndex = taskPackage.taskPackages[indexPackage].displayIndex;
      }

      if (indexTask < taskPackage.tasks.length) {
        taskIndex = taskPackage.tasks[indexTask].displayIndex;
      }

      if (parseInt(taskIndex) < parseInt(packIndex)) {
        if (taskIndex !== current.toString()) {
          taskPackage.tasks[indexTask].displayIndex = current.toString();
        }

        current++;
        indexTask++;
      } else {
        let pack = taskPackage.taskPackages[indexPackage];
        if (packIndex !== current.toString()) {
          pack.displayIndex = current.toString();
        }
        current++;
        indexPackage++;
        current = TaskPackage.FixIndex(pack, current);
      }
    }

    return current;
  }

  getTaskDND(taskDispIndex) {
    let filteredTasks = this.tasks.filter(
      (task) => task.displayIndex === taskDispIndex
    );

    if (filteredTasks[0] === undefined) {
      for (let i = 0; i < this.taskPackages.length; i++) {
        let task = this.taskPackages[i].getTaskDND(taskDispIndex);
        if (task != null) {
          return task;
        }
      }

      return null;
    } else {
      return {
        task: filteredTasks[0],
        parent: this,
      };
    }
  }

  maxIndexPackage() {
    let packageLast;
    if (this.taskPackages.length === 0) {
      packageLast = this.displayIndex;
    } else {
      const nextPack = this.taskPackages[this.taskPackages.length - 1];
      packageLast = nextPack.maxIndexPackage();
    }

    let taskLast;
    if (this.tasks.length === 0) {
      taskLast = 0;
    } else {
      taskLast = this.tasks[this.tasks.length - 1].displayIndex;
    }

    return parseInt(packageLast) >= parseInt(taskLast)
      ? parseInt(packageLast).toString()
      : parseInt(taskLast).toString();
  }

  static reIndexTask(pack, startIndex, endIndex, moved, change) {
    let index = pack.displayIndex;

    if (
      parseInt(startIndex) <= parseInt(index) &&
      parseInt(index) <= parseInt(endIndex) &&
      pack !== moved
    ) {
      pack.displayIndex = (parseInt(index) + change).toString();
    }

    pack.taskPackages.forEach((packg) => {
      TaskPackage.reIndexTask(packg, startIndex, endIndex, moved, change);
    });

    pack.tasks.forEach((task) => {
      let tIndex = task.displayIndex;
      if (
        startIndex <= parseInt(tIndex) &&
        parseInt(tIndex) <= parseInt(endIndex) &&
        task !== moved
      ) {
        task.displayIndex = (parseInt(tIndex) + change).toString();
      }
    });
  }

  reIndex(pack, startIndex, endIndex, movedPkg, changeUnit) {
    let index = pack.displayIndex;

    if (
      parseInt(index) >= parseInt(startIndex) &&
      parseInt(index) <= parseInt(endIndex)
    ) {
    }
  }

  getTaskPackageDND(taskPackageDispIndex) {
    if (this.displayIndex === taskPackageDispIndex) {
      return this;
    }

    let filteredTaskPackages = this.taskPackages.filter(
      (taskPkg) => taskPkg.displayIndex === taskPackageDispIndex
    );
    //let filteredTaskPackages = this.taskPackages.filter((taskPkg) => taskPkg.id === taskPackageId);
    if (filteredTaskPackages[0] !== undefined) {
      return {
        package: filteredTaskPackages[0],
        parent: this,
      };
    } else {
      for (let i = 0; i < this.taskPackages.length; i++) {
        let taskPkg =
          this.taskPackages[i].getTaskPackageDND(taskPackageDispIndex);
        if (taskPkg != null) {
          return taskPkg;
        }
      }
    }

    return null;
  }

  getTask(taskId) {
    let filteredTasks = this.tasks.filter((value) => value.id === taskId);
    if (filteredTasks.length > 0) {
      return filteredTasks[0];
    } else {
      if (this.taskPackages.length > 0) {
        for (let i = 0; i < this.taskPackages.length; i++) {
          let task = this.taskPackages[i].getTask(taskId);
          if (task != null) {
            return task;
          }
        }
      }
    }
  }

  getTaskByCode(code) {
    let filteredTasks = this.tasks.filter((value) => value.code === code);
    if (filteredTasks.length > 0) {
      return filteredTasks[0];
    } else {
      if (this.taskPackages.length > 0) {
        for (let i = 0; i < this.taskPackages.length; i++) {
          let task = this.taskPackages[i].getTaskByCode(code);
          if (task != null) {
            return task;
          }
        }
      }
    }
  }

  depthFirstTraversal() {
    let stack = [];
    stack.push(this);
    for (let tp of this.taskPackages) {
      if (Array.isArray(tp.taskPackages)) {
        stack = stack.concat(tp.depthFirstTraversal());
      }
    }

    if (Array.isArray(this.tasks)) {
      stack = stack.concat(this.tasks);
    }

    return stack;
  }

  getDisplayIndex(selectedTaskId) {
    let dft = this.depthFirstTraversal();
    let inst = dft.find((tp) => tp.id === selectedTaskId);
    return inst.displayIndex;
  }

  getTaskPackage(taskPackageId) {
    if (this.id === taskPackageId) {
      return this;
    }

    for (let i = 0; i < this.taskPackages.length; i++) {
      let task = this.taskPackages[i].getTaskPackage(taskPackageId);
      if (task != null) {
        return task;
      }
    }
  }

  getParentTaskPackage() {
    if (this.id === 0) {
      return null;
    }
    if (this.parent === 0) {
      return this.project.rootTaskPackage;
    }
    return this.project.rootTaskPackage
      .depthFirstTraversal()
      .find((tp) => tp.id === this.parent);
  }

  getActualProgressForPeriod(period) {
    if (this.actualProgress != null) {
      const filteredActual = this.actualProgress.filter(
        (entry) => entry.period === period
      );
      let result = filteredActual[0];
      if (result == null) {
        result = new ActualProgressEntry(
          this.project,
          this,
          period,
          null,
          null,
          null,
          null
        );
      }
      return result;
    }
  }

  getPlannedProgressForPeriod(period) {
    if (this.plannedProgress != null) {
      const filteredPlan = this.plannedProgress.filter(
        (entry) => entry.period === period
      );
      let result = filteredPlan[0];
      if (result == null) {
        result = new PlannedProgressEntry(
          this.project,
          this,
          period,
          null,
          null,
          null,
          null
        );
      }
      return result;
    }
  }

  getForecastedProgressForPeriod(period) {
    if (this.forecastedProgress != null) {
      const filteredforecast = this.forecastedProgress.filter(
        (entry) => entry.period === period
      );
      let result = filteredforecast[0];
      if (result == null) {
        result = new ForecastedProgressEntry(
          this.project,
          this,
          period,
          null,
          null
        );
      }
      return result;
    }
  }

  getCostWeightAllocation() {
    if (this.costWeightAllocation !== "inherited") {
      return this.costWeightAllocation;
    }

    return this.parent === ""
      ? this.costWeightAllocation
      : this.getParentTaskPackage().getCostWeightAllocation();
  }

  // Sends task package to server side and updates the duration and periods
  updateTaskPackage() {
    UpdateTaskPackage(this.project, this, (tp) => {
      let taskPackage = TaskPackage.fromJS(this.project, tp, tp.parent);
      this.plannedDuration = taskPackage.plannedDuration;
      this.plannedStartPeriod = Math.min(
        ...taskPackage.plannedProgress.map(
          (plannedProgressEntry) => plannedProgressEntry.period
        )
      );
      this.plannedEndPeriod = Math.max(
        ...taskPackage.plannedProgress.map(
          (plannedProgressEntry) => plannedProgressEntry.period
        )
      );
      this.plannedProgress = taskPackage.plannedProgress;
      this.actualProgress = taskPackage.actualProgress;

      this.plannedStartDate = taskPackage.plannedStartDate;
      this.plannedEndDate = taskPackage.plannedEndDate;

      updateParentTaskPackage(this);
    });
  }

  updateParentTaskPackageActuals() {
    let parent = this.getParentTaskPackage();
    debugger;
    if (!isNullOrUndefined(parent)) {
      UpdateActuals(this.project, parent, (tp) => {
        let taskPackage = TaskPackage.fromJS(this.project, tp, tp.parent);
        parent.actualProgress = taskPackage.actualProgress;
      });
      parent.updateParentTaskPackageActuals();
    }
  }

  getRealForecastMethod() {
    if (this.forecastMethod && this.forecastMethod !== "Default") {
      return this.forecastMethod;
    } else {
      return this.getParentTaskPackage()?.getRealForecastMethod() ?? "None";
    }
  }

  toJS() {
    return {
      id: this.id,
      name: this.name,
      parent: this.parent,
      displayIndex: this.displayIndex,
      parentDisplayIndex: this.parentDisplayIndex,
      forecastMethod: this.forecastMethod,
      constantForcastValue: this.constantForcastValue,
      plannedCost: !isNullOrUndefined(this.plannedCost)
        ? +this.plannedCost
        : null,
      plannedDuration: this.plannedDuration,
      plannedStartDate: !isNullOrUndefined(this.plannedStartDate)
        ? new Date(this.plannedStartDate).toISOString()
        : null,
      plannedEndDate: !isNullOrUndefined(this.plannedEndDate)
        ? new Date(this.plannedEndDate).toISOString()
        : null,
      costWeightAllocation: this.costWeightAllocation,
      durationWeightAllocation: this.durationWeightAllocation,
      tasks: this.tasks.map((entry) => entry.toJS()),
      taskPackages: this.taskPackages.map((entry) => entry.toJS()),
      actualProgress: this.actualProgress.map((entry) => entry.toJS()),
      plannedProgress: this.plannedProgress.map((entry) => entry.toJS()),
      forecastedProgress: this.forecastedProgress.map((entry) => entry.toJS()),
      forecastedEndPeriod: this.forecastedEndPeriod,
      code: this.code,
      oldId: this.oldId,
    };
  }

  static fromJS(store, object, parent) {
    const taskPackage = new TaskPackage();
    if (object != null) {
      taskPackage.oldId = object.oldId;
      taskPackage.name = object.name;
      taskPackage.id = object.id;
      taskPackage.displayIndex = object.displayIndex;
      taskPackage.code = object.code;
      taskPackage.project = store;
      taskPackage.parent = parent;
      taskPackage.parentDisplayIndex = object.parentDisplayIndex;
      taskPackage.forecastMethod = object.forecastMethod;
      taskPackage.constantForcastValue = object.constantForcastValue;
      taskPackage.plannedCost = object.plannedCost;
      taskPackage.plannedDuration = object.plannedDuration;
      taskPackage.plannedStartDate = object.plannedStartDate;
      taskPackage.plannedEndDate = object.plannedEndDate;
      taskPackage.forecastedEndPeriod = object.forecastedEndPeriod;

      if (isNullOrUndefined(object.costWeightAllocation)) {
        taskPackage.costWeightAllocation = parent === "" ? "50" : "inherited";
      } else {
        taskPackage.costWeightAllocation = object.costWeightAllocation;
      }

      if (isNullOrUndefined(object.durationWeightAllocation)) {
        taskPackage.durationWeightAllocation =
          parent === "" ? "50" : "inherited";
      } else {
        taskPackage.durationWeightAllocation = object.durationWeightAllocation;
      }

      taskPackage.tasks = object.tasks.map((savedTask) => {
        return Task.fromJS(store, savedTask, object.id);
      });
      taskPackage.taskPackages = object.taskPackages.map((savedTaskpackage) => {
        return TaskPackage.fromJS(store, savedTaskpackage, object.id);
      });

      if (isNullOrUndefined(object.actualProgress)) {
        taskPackage.actualProgress = [];
      } else {
        taskPackage.actualProgress = object.actualProgress.map((entry) =>
          ActualProgressEntry.fromJS(store, entry)
        );
        taskPackage.actualProgress.forEach((element) => {
          element.taskPackage = taskPackage;
        });
      }

      if (isNullOrUndefined(object.plannedProgress)) {
        taskPackage.plannedProgress = [];
      } else {
        taskPackage.plannedProgress = object.plannedProgress.map((entry) =>
          PlannedProgressEntry.fromJS(store, entry)
        );
        taskPackage.plannedProgress.forEach((element) => {
          element.taskPackage = taskPackage;
        });
      }

      if (isNullOrUndefined(object.forecastedProgress)) {
        taskPackage.forecastedProgress = [];
      } else {
        taskPackage.forecastedProgress = object.forecastedProgress.map(
          (entry) => ForecastedProgressEntry.fromJS(store, entry)
        );
        taskPackage.forecastedProgress.forEach((element) => {
          element.taskPackage = taskPackage;
        });
      }

      if (isNullOrUndefined(taskPackage.plannedProgress)) {
        taskPackage.plannedStartPeriod = 0;
        taskPackage.plannedEndPeriod = 0;
      } else {
        taskPackage.plannedStartPeriod = Math.min(
          ...taskPackage.plannedProgress.map((entry) => entry.period)
        );
        taskPackage.plannedEndPeriod = Math.max(
          ...taskPackage.plannedProgress.map((entry) => entry.period)
        );
      }
    }
    return taskPackage;
  }
}


